import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useNavigate, useLocation } from 'react-router-dom';

const CLOUD_FUNCTIONS_BASE = 'https://us-central1-skilltrade-app-2026.cloudfunctions.net';

const TRADE_DATA = {
  'Electrical': {
    requiresLicense: true,
    licenseBoard: 'NC Electrical Contractors Board',
    licenseNote: 'Required at Master/Foreman level only — no individual journeyman license in NC',
    specializations: [
      'Low Voltage (LV)', 'Medium Voltage (MV)', 'High Voltage (HV)',
      'Data/Structured Cabling', 'Fire Alarm', 'Security Systems',
      'Audio Visual', 'Solar/Renewable Energy', 'EV Charging',
      'Controls/PLC', 'Instrumentation', 'Lighting Controls',
    ],
    licensedSpecializations: ['Fire Alarm', 'Medium Voltage (MV)', 'High Voltage (HV)', 'Solar/Renewable Energy', 'EV Charging'],
  },
  'Plumbing': {
    requiresLicense: true,
    licenseBoard: 'NC Plumbing, Heating, Fire Sprinkler Board',
    licenseNote: 'Required at Master/Foreman level only — no individual journeyman license in NC',
    specializations: [
      'Water/Supply', 'Drain/Waste/Vent', 'Gas Piping',
      'Backflow Prevention', 'Medical Gas', 'Fire Suppression', 'Hydronic Heating',
    ],
    licensedSpecializations: ['Gas Piping', 'Backflow Prevention', 'Medical Gas', 'Fire Suppression'],
  },
  'HVAC': {
    requiresLicense: true,
    licenseBoard: 'NC Plumbing, Heating, Fire Sprinkler Board',
    licenseNote: 'Required at Master/Foreman level only — EPA 608 required for refrigerant handling',
    specializations: [
      'Sheet Metal', 'Refrigeration', 'Building Controls/BMS',
      'Ductless Mini Split', 'Commercial Rooftop Units', 'Industrial Process Cooling',
    ],
    licensedSpecializations: ['Refrigeration'],
    certRequired: ['EPA 608'],
  },
  'Roofing': {
    requiresLicense: true,
    licenseBoard: 'NC Licensing Board for General Contractors',
    licenseNote: 'Required for projects over $40,000',
    specializations: ['Shingle', 'Metal Roofing', 'Flat/TPO/EPDM', 'Tile', 'Repair/Maintenance'],
    licensedSpecializations: [],
  },
  'Carpentry': {
    requiresLicense: false,
    specializations: ['Framing', 'Finish Carpentry', 'Formwork/Concrete Forming', 'Cabinet Installation', 'Millwork'],
  },
  'Welding': {
    requiresLicense: false,
    specializations: ['Stick/SMAW', 'MIG/GMAW', 'TIG/GTAW', 'Structural', 'Pipe Welding'],
  },
  'Painting': {
    requiresLicense: false,
    specializations: ['Interior', 'Exterior', 'Commercial', 'Residential', 'Industrial Coatings'],
  },
  'Concrete': {
    requiresLicense: false,
    specializations: ['Flatwork', 'Foundations', 'Decorative Concrete', 'Formwork', 'Pumping'],
  },
  'Drywall': {
    requiresLicense: false,
    specializations: ['Hanging', 'Finishing/Taping', 'Metal Framing', 'Acoustical Ceilings', 'Repair'],
  },
  'Pipefitting': {
    requiresLicense: false,
    specializations: ['Steam', 'Process Piping', 'HVAC Piping', 'Gas Piping', 'Hydronics'],
  },
  'General Labor': {
    requiresLicense: false,
    specializations: ['Site Cleanup', 'Material Handling', 'Demolition', 'Concrete Labor', 'Flagging/Traffic Control'],
  },
  'Landscaping': {
    requiresLicense: false,
    specializations: ['Lawn Care', 'Hardscaping', 'Irrigation', 'Tree Work', 'Snow Removal'],
  },
};

const INDUSTRIES = ['Residential', 'Commercial', 'Industrial', 'Service/Maintenance'];

const LEVELS = [
  { value: 'Helper', label: 'Helper / Laborer', years: '0–2 years', description: 'Works under supervision, entry level — goes active immediately, no license required' },
  { value: 'Apprentice', label: 'Apprentice', years: '2–4 years', description: 'In training, developing skills — goes active immediately, no license required' },
  { value: 'Journeyman', label: 'Journeyman', years: '4–8 years', description: 'Works independently — goes active immediately, no state journeyman license in NC' },
  { value: 'Master', label: 'Master / Foreman', years: '8+ years', description: 'Supervisory capable — contractor license required in NC to pull permits' },
];

const CERTIFICATIONS = [
  'OSHA 10', 'OSHA 30', 'First Aid/CPR', 'Forklift Certified',
  'Scissor Lift / AWP Certified', 'Confined Space Entry', 'Fall Protection',
  'NCCER Certification', 'AWS Certified Welder', 'EPA 608 Universal',
  'EPA 608 Type I', 'EPA 608 Type II', 'EPA 608 Type III',
  'Backflow Prevention Certified', 'Medical Gas (ASSE 6000)', 'ATSSA Flagger Certification',
];

const WORKER_AGREEMENTS = [
  { id: 'independent_contractor', text: 'I understand I am an independent contractor, not an employee of TradePower' },
  { id: 'marketplace_only', text: 'I understand TradePower is a technology marketplace only and does not direct or control my work' },
  { id: 'taxes', text: 'I am responsible for my own taxes and will receive a 1099-NEC if I earn over $600 annually' },
  { id: 'tools', text: 'I am responsible for my own tools, equipment, and transportation' },
  { id: 'work_authorized', text: 'I am authorized to work in the United States' },
  { id: 'liability', text: 'I carry my own liability for work performed on job sites' },
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

const getLevelFromYears = (years) => {
  if (years >= 8) return 'Master';
  if (years >= 4) return 'Journeyman';
  if (years >= 2) return 'Apprentice';
  return 'Helper';
};

const getTradeStatus = (tradeProfile) => {
  const tradeInfo = TRADE_DATA[tradeProfile.trade];
  if (!tradeInfo?.requiresLicense || tradeProfile.level !== 'Master') {
    return { status: 'active', label: '✅ Active', detail: 'No license required at this level in NC', color: '#155724', bg: '#d4edda' };
  }
  if (tradeProfile.licenseStatus === 'verified') {
    return { status: 'active_licensed', label: '✅ Active — Licensed Contractor', detail: 'Appears in all searches including Licensed Only', color: '#155724', bg: '#d4edda' };
  }
  if (tradeProfile.licenseStatus === 'pending') {
    return { status: 'pending', label: '⏳ Active — License Pending', detail: 'Visible now, Licensed badge activates after review', color: '#856404', bg: '#fff3cd' };
  }
  return { status: 'unlicensed', label: '⚠️ Active — Not in Licensed Searches', detail: 'Upload contractor license to appear in Licensed Only searches', color: '#856404', bg: '#fff8e1' };
};

function WorkerProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = auth.currentUser;

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [dob, setDob] = useState('');
  const [homeAddress, setHomeAddress] = useState('');
  const [homeCity, setHomeCity] = useState('');
  const [homeState, setHomeState] = useState('');
  const [homeZip, setHomeZip] = useState('');
  const [workState, setWorkState] = useState('NC');
  const [zipCode, setZipCode] = useState('');
  const [radius, setRadius] = useState('25');
  const [photoData, setPhotoData] = useState('');
  const [bio, setBio] = useState('');
  const [hourlyRate, setHourlyRate] = useState('');
  const [dlState, setDlState] = useState('');
  const [dlNumber, setDlNumber] = useState('');
  const [tradeProfiles, setTradeProfiles] = useState([]);
  const [showAddTrade, setShowAddTrade] = useState(false);
  const [newTrade, setNewTrade] = useState('');
  const [newLevel, setNewLevel] = useState('');
  const [newYears, setNewYears] = useState('');
  const [newIndustries, setNewIndustries] = useState([]);
  const [newSpecializations, setNewSpecializations] = useState([]);
  const [newLicenseNumber, setNewLicenseNumber] = useState('');
  const [newLicenseType, setNewLicenseType] = useState('');
  const [newLicenseState, setNewLicenseState] = useState('NC');
  const [newLicenseExpiry, setNewLicenseExpiry] = useState('');
  const [newLicenseDoc, setNewLicenseDoc] = useState('');
  const [certifications, setCertifications] = useState([]);
  const [complianceAgreements, setComplianceAgreements] = useState({});
  const [insuranceVerified, setInsuranceVerified] = useState(false);
  const [insuranceDoc, setInsuranceDoc] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('basic');
  const [stateMismatch, setStateMismatch] = useState(false);

  const [stripeOnboardingDone, setStripeOnboardingDone] = useState(false);
  const [stripePayoutsEnabled, setStripePayoutsEnabled] = useState(false);
  const [stripeRedirecting, setStripeRedirecting] = useState(false);
  const [payrollLedger, setPayrollLedger] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('stripe') === 'success') {
      checkStripeStatus();
      window.history.replaceState({}, '', '/worker-profile');
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const loadProfile = async () => {
      if (!user) return;
      try {
        const docSnap = await getDoc(doc(db, 'workerProfiles', user.uid));
        if (docSnap.exists()) {
          const data = docSnap.data();
          setName(data.name || '');
          setPhone(data.phone || '');
          setDob(data.dob || '');
          setHomeAddress(data.homeAddress || '');
          setHomeCity(data.homeCity || '');
          setHomeState(data.homeState || '');
          setHomeZip(data.homeZip || '');
          setWorkState(data.workState || 'NC');
          setZipCode(data.zipCode || '');
          setRadius(data.radius?.toString() || '25');
          setPhotoData(data.photoData || '');
          setBio(data.bio || '');
          setHourlyRate(data.hourlyRate?.toString() || '');
          setDlState(data.dlState || '');
          setDlNumber(data.dlNumber || '');
          setTradeProfiles(data.tradeProfiles || []);
          setCertifications(data.certifications || []);
          setComplianceAgreements(data.agreements || {});
          setInsuranceVerified(data.insuranceVerified || false);
          setInsuranceDoc(data.insuranceDoc || '');
          setStripeOnboardingDone(data.stripeOnboardingDone || false);
          setStripePayoutsEnabled(data.stripePayoutsEnabled || false);
          if (data.homeState && data.workState && data.homeState !== data.workState) {
            setStateMismatch(true);
          }
        }
        // Load smsEnabled from users collection
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        if (userSnap.exists()) {
          const userData = userSnap.data();
          setSmsEnabled(userData.smsEnabled || false);
        }
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    loadProfile();
  }, [user]);

  useEffect(() => {
    if (newYears) setNewLevel(getLevelFromYears(parseInt(newYears)));
  }, [newYears]);

  const checkStripeStatus = async () => {
    if (!user) return;
    try {
      const res = await fetch(`${CLOUD_FUNCTIONS_BASE}/checkWorkerStripeStatus`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workerId: user.uid }),
      });
      const data = await res.json();
      setStripeOnboardingDone(data.onboardingDone || false);
      setStripePayoutsEnabled(data.payoutsEnabled || false);
    } catch (err) { console.error(err); }
  };

  const handleStartStripeOnboarding = async () => {
    setStripeRedirecting(true);
    try {
      const workerDoc = await getDoc(doc(db, 'workerProfiles', user.uid));
      const workerData = workerDoc.data() || {};
      const res = await fetch(`${CLOUD_FUNCTIONS_BASE}/createWorkerStripeAccount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workerId: user.uid,
          workerEmail: user.email,
          workerName: workerData.name || user.email,
          returnUrl: `${window.location.origin}/worker-profile?stripe=success`,
          refreshUrl: `${window.location.origin}/worker-profile?stripe=refresh`,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert('Error starting Stripe setup. Please try again.');
        setStripeRedirecting(false);
      }
    } catch (err) {
      console.error(err);
      setStripeRedirecting(false);
    }
  };

  const loadPayrollLedger = async () => {
    if (!user) return;
    setLedgerLoading(true);
    try {
      const q = query(collection(db, 'payroll_ledger'), where('workerId', '==', user.uid));
      const snap = await getDocs(q);
      const entries = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => b.date?.localeCompare(a.date));
      setPayrollLedger(entries);
    } catch (err) { console.error(err); }
    setLedgerLoading(false);
  };

  useEffect(() => {
    if (activeSection === 'payments') loadPayrollLedger();
  }, [activeSection]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoData(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleLicenseDocUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('File too large. Max 5MB.'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setNewLicenseDoc(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleInsuranceUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setInsuranceDoc(ev.target.result);
    reader.readAsDataURL(file);
  };

  const toggleIndustry = (industry) => {
    setNewIndustries(prev =>
      prev.includes(industry) ? prev.filter(i => i !== industry) : [...prev, industry]
    );
  };

  const toggleSpecialization = (spec) => {
    setNewSpecializations(prev =>
      prev.includes(spec) ? prev.filter(s => s !== spec) : [...prev, spec]
    );
  };

  const toggleCertification = (cert) => {
    setCertifications(prev =>
      prev.includes(cert) ? prev.filter(c => c !== cert) : [...prev, cert]
    );
  };

  const toggleComplianceAgreement = (id) => {
    setComplianceAgreements(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const addTradeProfile = () => {
    if (!newTrade || !newLevel || !newYears) {
      alert('Please fill in trade, level, and years of experience.');
      return;
    }
    if (newIndustries.length === 0) {
      alert('Please select at least one industry.');
      return;
    }
    const tradeInfo = TRADE_DATA[newTrade];
    const requiresLicense = tradeInfo?.requiresLicense && newLevel === 'Master';
    const newProfile = {
      id: Date.now().toString(),
      trade: newTrade, level: newLevel,
      yearsExperience: parseInt(newYears),
      industries: newIndustries, specializations: newSpecializations,
      requiresLicense, licenseNumber: newLicenseNumber,
      licenseType: newLicenseType, licenseState: newLicenseState,
      licenseExpiry: newLicenseExpiry, licenseDoc: newLicenseDoc,
      licenseStatus: newLicenseDoc ? 'pending' : 'none',
      submittedAt: newLicenseDoc ? new Date().toISOString() : null,
    };
    setTradeProfiles(prev => [...prev, newProfile]);
    setNewTrade(''); setNewLevel(''); setNewYears('');
    setNewIndustries([]); setNewSpecializations([]);
    setNewLicenseNumber(''); setNewLicenseType('');
    setNewLicenseState('NC'); setNewLicenseExpiry('');
    setNewLicenseDoc(''); setShowAddTrade(false);
  };

  const removeTradeProfile = (id) => {
    setTradeProfiles(prev => prev.filter(t => t.id !== id));
  };

  const getLicenseStatusBadge = (profile) => {
    const tradeInfo = TRADE_DATA[profile.trade];
    if (!tradeInfo?.requiresLicense) return null;
    if (profile.level !== 'Master') return null;
    if (profile.licenseStatus === 'verified') return { color: '#155724', bg: '#d4edda', text: '✅ Contractor License Verified' };
    if (profile.licenseStatus === 'pending') return { color: '#856404', bg: '#fff3cd', text: '⏳ License Pending Review' };
    return { color: '#721c24', bg: '#f8d7da', text: '⚠️ Contractor License Not On File' };
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setDeleteError('Please type DELETE to confirm.');
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      const res = await fetch(`${CLOUD_FUNCTIONS_BASE}/deleteUserAccount`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.uid, userType: 'worker' }),
      });
      const data = await res.json();
      if (data.blocked) {
        setDeleteError(data.message);
        setDeleting(false);
        return;
      }
      if (data.success) {
        await auth.signOut();
        navigate('/login');
      } else {
        setDeleteError('Something went wrong. Please try again or contact support.');
      }
    } catch (err) {
      setDeleteError('Something went wrong. Please try again or contact support.');
    }
    setDeleting(false);
  };

  const isProfileActive = name && hourlyRate && tradeProfiles.length > 0;
  const allComplianceChecked = WORKER_AGREEMENTS.every(a => complianceAgreements[a.id]);

  const handleSave = async () => {
    if (!name.trim()) { alert('Please enter your name.'); return; }
    if (!hourlyRate) { alert('Please enter your hourly rate.'); return; }
    if (tradeProfiles.length === 0) { alert('Please add at least one trade profile.'); return; }
    if (smsEnabled && !phone.trim()) {
      alert('Please enter a phone number to enable SMS alerts.');
      return;
    }
    setSaving(true);
    try {
      const trades = [...new Set(tradeProfiles.map(t => t.trade))];
      const workTypes = [...new Set(tradeProfiles.flatMap(t => t.industries))];
      const licenses = tradeProfiles
        .filter(t => t.licenseNumber)
        .map(t => ({
          state: t.licenseState, number: t.licenseNumber,
          type: t.licenseType, trade: t.trade,
          status: t.licenseStatus, expiry: t.licenseExpiry,
        }));

      // Save to workerProfiles
      await setDoc(doc(db, 'workerProfiles', user.uid), {
        name, email: user.email, phone, dob,
        homeAddress, homeCity, homeState, homeZip,
        workState, zipCode, radius: parseInt(radius),
        photoData, bio, hourlyRate: parseFloat(hourlyRate),
        dlState, dlNumber, tradeProfiles, certifications,
        trades, workTypes, licenses,
        agreements: complianceAgreements,
        workAuthorized: complianceAgreements['work_authorized'] || false,
        w9Consent: complianceAgreements['taxes'] || false,
        backgroundCheckConsent: false,
        insuranceVerified, insuranceDoc,
        updatedAt: new Date(),
      }, { merge: true });

      // Save phone + smsEnabled to users collection (where Cloud Functions read from)
      await setDoc(doc(db, 'users', user.uid), {
        phone, smsEnabled,
      }, { merge: true });

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
      alert('Error saving profile. Please try again.');
    }
    setSaving(false);
  };

  const getLedgerByWeek = () => {
    const weeks = {};
    payrollLedger.forEach(entry => {
      const week = entry.weekEnding || 'Unknown';
      if (!weeks[week]) {
        weeks[week] = { weekEnding: week, payoutDate: entry.payoutDate, entries: [], totalHours: 0, totalEarnings: 0, status: entry.status };
      }
      weeks[week].entries.push(entry);
      weeks[week].totalHours += entry.confirmedHours || 0;
      weeks[week].totalEarnings += entry.workerEarnings || 0;
      if (entry.status === 'paid') weeks[week].status = 'paid';
    });
    return Object.values(weeks).sort((a, b) => b.weekEnding.localeCompare(a.weekEnding));
  };

  const totalEarningsAllTime = payrollLedger.filter(e => e.status === 'paid').reduce((sum, e) => sum + (e.workerEarnings || 0), 0);
  const pendingEarnings = payrollLedger.filter(e => e.status === 'ready').reduce((sum, e) => sum + (e.workerEarnings || 0), 0);

  if (loading) return <div style={styles.loadingContainer}><p>Loading profile...</p></div>;

  const selectedTradeInfo = TRADE_DATA[newTrade];

  return (
    <div style={styles.container}>
      <div style={styles.navbar}>
        <h1 style={{ ...styles.logo, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>TradePower</h1>
        <div style={styles.navRight}>
          <button style={saved ? styles.savedBtn : saving ? styles.savingBtn : styles.saveBtn} onClick={handleSave} disabled={saving}>
            {saved ? '✅ Saved!' : saving ? 'Saving...' : 'Save Profile'}
          </button>
          <button style={styles.backButton} onClick={() => navigate('/dashboard')}>← Back</button>
        </div>
      </div>

      <div style={styles.content}>
        <div style={styles.header}>
          <h2 style={styles.title}>Worker Profile</h2>
          <p style={styles.subtitle}>Build your profile to get hired by contractors on TradePower</p>
        </div>

        {stateMismatch && (
          <div style={styles.mismatchWarning}>
            ⚠️ Your home state ({homeState}) doesn't match your work state ({workState}).
          </div>
        )}

        {/* STATUS PANEL */}
        <div style={styles.statusPanel}>
          <div style={styles.statusPanelHeader}>
            <div>
              <h3 style={styles.statusPanelTitle}>📊 Profile Status</h3>
              <p style={styles.statusPanelSubtitle}>What contractors can currently see</p>
            </div>
            <span style={isProfileActive ? styles.activeBadge : styles.inactiveBadge}>
              {isProfileActive ? '🟢 Active' : '⚠️ Inactive'}
            </span>
          </div>
          {!isProfileActive ? (
            <div style={styles.statusNotActive}>
              Complete all required fields and save your profile to become active:
              <div style={styles.statusChecklist}>
                <span style={name ? styles.checkDone : styles.checkMissing}>{name ? '✅' : '○'} Full name</span>
                <span style={hourlyRate ? styles.checkDone : styles.checkMissing}>{hourlyRate ? '✅' : '○'} Hourly rate</span>
                <span style={tradeProfiles.length > 0 ? styles.checkDone : styles.checkMissing}>{tradeProfiles.length > 0 ? '✅' : '○'} At least one trade profile</span>
                <span style={allComplianceChecked ? styles.checkDone : styles.checkMissing}>{allComplianceChecked ? '✅' : '○'} Compliance agreements</span>
              </div>
            </div>
          ) : (
            <div style={styles.statusTradeList}>
              {tradeProfiles.map(tp => {
                const ts = getTradeStatus(tp);
                return (
                  <div key={tp.id} style={styles.statusTradeRow}>
                    <div style={styles.statusTradeInfo}>
                      <span style={styles.statusTradeName}>{tp.trade}</span>
                      <span style={styles.statusTradeLevel}>{tp.level} • {tp.yearsExperience} yrs • {tp.industries?.join(', ')}</span>
                      {tp.specializations?.length > 0 && <span style={styles.statusTradeSpecs}>{tp.specializations.join(' • ')}</span>}
                    </div>
                    <div style={styles.statusTradeBadgeCol}>
                      <span style={{ ...styles.statusTradeBadge, backgroundColor: ts.bg, color: ts.color }}>{ts.label}</span>
                      <span style={styles.statusTradeDetail}>{ts.detail}</span>
                    </div>
                  </div>
                );
              })}
              {certifications.length > 0 && (
                <div style={styles.statusCertRow}>
                  <span style={styles.statusCertLabel}>🏆 Certifications:</span>
                  <div style={styles.statusCertTags}>
                    {certifications.map(cert => <span key={cert} style={styles.statusCertTag}>{cert}</span>)}
                  </div>
                </div>
              )}
              {hourlyRate && (
                <div style={styles.statusRateRow}>
                  <span style={styles.statusRateLabel}>💰 Your Rate:</span>
                  <span style={styles.statusRateValue}>${hourlyRate}/hr</span>
                  <span style={styles.statusRateContractor}>Contractors see ${Math.ceil(parseFloat(hourlyRate) * 1.15)}/hr</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* SECTION NAV */}
        <div style={styles.sectionNav}>
          {[
            { id: 'basic', label: '👤 Basic Info' },
            { id: 'trades', label: '🔧 Trade Profiles' },
            { id: 'certifications', label: '🏆 Certifications' },
            { id: 'work', label: '💼 Work Preferences' },
            { id: 'compliance', label: allComplianceChecked ? '✅ Compliance' : '⚠️ Compliance' },
            { id: 'payments', label: stripeOnboardingDone ? '💰 Payments ✅' : '💰 Payments ⚠️' },
          ].map(section => (
            <button key={section.id}
              style={activeSection === section.id ? styles.sectionNavBtnActive : styles.sectionNavBtn}
              onClick={() => setActiveSection(section.id)}>
              {section.label}
            </button>
          ))}
        </div>

        {/* BASIC INFO */}
        {activeSection === 'basic' && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Basic Information</h3>
            <div style={styles.photoSection}>
              {photoData ? (
                <img src={photoData} alt="Profile" style={styles.photo} />
              ) : (
                <div style={styles.photoPlaceholder}><span style={{ fontSize: '48px' }}>👤</span></div>
              )}
              <div>
                <label style={styles.uploadBtn}>
                  📷 Upload Photo
                  <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} />
                </label>
                <p style={styles.uploadNote}>JPG or PNG, professional headshot recommended</p>
              </div>
            </div>
            <div style={styles.row}>
              <div style={{ flex: 1 }}>
                <label style={styles.fieldLabel}>Full Name *</label>
                <input style={styles.input} type="text" placeholder="Your full name" value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.fieldLabel}>Phone Number</label>
                <input style={styles.input} type="tel" placeholder="(704) 555-0100" value={phone} onChange={e => setPhone(e.target.value)} />
              </div>
            </div>

            {/* SMS Toggle */}
            <div style={styles.smsToggleBox}>
              <div style={styles.smsToggleLeft}>
                <p style={styles.smsToggleTitle}>📱 Text Message Alerts</p>
                <p style={styles.smsToggleDesc}>
                  Receive SMS alerts for new job requests, messages, and payouts.
                  {!phone.trim() && ' Enter your phone number above to enable.'}
                </p>
              </div>
              <button
                type="button"
                style={smsEnabled ? styles.smsToggleOn : styles.smsToggleOff}
                onClick={() => {
                  if (!phone.trim() && !smsEnabled) {
                    alert('Please enter your phone number first.');
                    return;
                  }
                  setSmsEnabled(!smsEnabled);
                }}>
                {smsEnabled ? 'ON' : 'OFF'}
              </button>
            </div>

            <div style={styles.row}>
              <div style={{ flex: 1 }}>
                <label style={styles.fieldLabel}>Date of Birth</label>
                <input style={styles.input} type="date" value={dob} onChange={e => setDob(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.fieldLabel}>Work State</label>
                <select style={styles.input} value={workState} onChange={e => setWorkState(e.target.value)}>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <h4 style={styles.subheading}>Home Address</h4>
            <input style={styles.input} type="text" placeholder="Street Address" value={homeAddress} onChange={e => setHomeAddress(e.target.value)} />
            <div style={styles.row}>
              <div style={{ flex: 2 }}>
                <label style={styles.fieldLabel}>City</label>
                <input style={styles.input} type="text" placeholder="Charlotte" value={homeCity} onChange={e => setHomeCity(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.fieldLabel}>State</label>
                <select style={styles.input} value={homeState}
                  onChange={e => { setHomeState(e.target.value); setStateMismatch(e.target.value !== workState && e.target.value !== ''); }}>
                  <option value="">Select</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.fieldLabel}>ZIP</label>
                <input style={styles.input} type="text" placeholder="28209" maxLength={5} value={homeZip} onChange={e => setHomeZip(e.target.value)} />
              </div>
            </div>
            <h4 style={styles.subheading}>Driver's License</h4>
            <div style={styles.row}>
              <div style={{ flex: 1 }}>
                <label style={styles.fieldLabel}>DL State</label>
                <select style={styles.input} value={dlState} onChange={e => setDlState(e.target.value)}>
                  <option value="">Select State</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ flex: 2 }}>
                <label style={styles.fieldLabel}>DL Number</label>
                <input style={styles.input} type="text" placeholder="License number" value={dlNumber} onChange={e => setDlNumber(e.target.value)} />
              </div>
            </div>
            <div style={styles.nextBtn}>
              <button style={styles.primaryBtn} onClick={() => setActiveSection('trades')}>Next: Trade Profiles →</button>
            </div>
          </div>
        )}

        {/* TRADE PROFILES */}
        {activeSection === 'trades' && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Trade Profiles</h3>
            <p style={styles.sectionSubtitle}>Add all trades you work in.</p>
            {tradeProfiles.length === 0 && !showAddTrade && (
              <div style={styles.emptyTrades}>
                <p style={{ fontSize: '48px', marginBottom: '12px' }}>🔧</p>
                <p style={styles.emptyTitle}>No trades added yet</p>
                <p style={styles.emptyText}>Add your first trade to start getting hired</p>
              </div>
            )}
            {tradeProfiles.map(profile => {
              const statusBadge = getLicenseStatusBadge(profile);
              const tradeInfo = TRADE_DATA[profile.trade];
              return (
                <div key={profile.id} style={styles.tradeCard}>
                  <div style={styles.tradeCardHeader}>
                    <div>
                      <h4 style={styles.tradeName}>{profile.trade}</h4>
                      <div style={styles.tradeBadgeRow}>
                        <span style={styles.levelBadge}>{profile.level}</span>
                        <span style={styles.yearsBadge}>{profile.yearsExperience} yrs exp</span>
                        {statusBadge && <span style={{ ...styles.licenseBadge, backgroundColor: statusBadge.bg, color: statusBadge.color }}>{statusBadge.text}</span>}
                        {!tradeInfo?.requiresLicense && <span style={styles.noLicenseBadge}>✅ No License Required</span>}
                        {tradeInfo?.requiresLicense && profile.level !== 'Master' && <span style={styles.noLicenseBadge}>✅ Active — No License at This Level</span>}
                      </div>
                    </div>
                    <button style={styles.removeBtn} onClick={() => removeTradeProfile(profile.id)}>✕ Remove</button>
                  </div>
                  <div style={styles.tradeDetails}>
                    {profile.industries.length > 0 && (
                      <div style={styles.tradeDetailRow}>
                        <span style={styles.tradeDetailLabel}>Industries:</span>
                        <div style={styles.tagRow}>{profile.industries.map(i => <span key={i} style={styles.industryTag}>{i}</span>)}</div>
                      </div>
                    )}
                    {profile.specializations.length > 0 && (
                      <div style={styles.tradeDetailRow}>
                        <span style={styles.tradeDetailLabel}>Specializations:</span>
                        <div style={styles.tagRow}>{profile.specializations.map(s => <span key={s} style={styles.specTag}>{s}</span>)}</div>
                      </div>
                    )}
                    {profile.licenseNumber && (
                      <div style={styles.tradeDetailRow}>
                        <span style={styles.tradeDetailLabel}>License:</span>
                        <span style={{ fontSize: '13px', color: '#333' }}>{profile.licenseState} {profile.licenseType} #{profile.licenseNumber}{profile.licenseExpiry && ` — Exp: ${profile.licenseExpiry}`}</span>
                      </div>
                    )}
                  </div>
                  {tradeInfo?.requiresLicense && profile.level === 'Master' && profile.licenseStatus !== 'verified' && (
                    <div style={styles.licenseInfoBox}>
                      <p style={styles.licenseInfoText}>
                        {profile.licenseStatus === 'pending'
                          ? '⏳ Your contractor license is pending admin review.'
                          : `⚠️ Master/Foreman level in ${profile.trade} requires a ${tradeInfo.licenseBoard} contractor license.`}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
            {showAddTrade ? (
              <div style={styles.addTradeForm}>
                <h4 style={styles.addTradeTitle}>Add Trade Profile</h4>
                <label style={styles.fieldLabel}>Trade *</label>
                <select style={styles.input} value={newTrade} onChange={e => { setNewTrade(e.target.value); setNewSpecializations([]); }}>
                  <option value="">Select a trade</option>
                  {Object.keys(TRADE_DATA).map(trade => <option key={trade} value={trade}>{trade}</option>)}
                </select>
                {selectedTradeInfo?.requiresLicense && (
                  <div style={styles.licenseInfoBanner}>
                    🏛️ <strong>{newTrade}</strong> is a licensed trade in NC at the contractor level.
                    {newLevel === 'Master' ? ` As a Master/Foreman you'll need a ${selectedTradeInfo.licenseBoard} contractor license.` : ' Helpers, Apprentices, and Journeymen go active immediately.'}
                  </div>
                )}
                <div style={styles.row}>
                  <div style={{ flex: 1 }}>
                    <label style={styles.fieldLabel}>Years of Experience *</label>
                    <input style={styles.input} type="number" min="0" max="50" placeholder="e.g. 5" value={newYears} onChange={e => setNewYears(e.target.value)} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={styles.fieldLabel}>Level *</label>
                    <select style={styles.input} value={newLevel} onChange={e => setNewLevel(e.target.value)}>
                      <option value="">Select level</option>
                      {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label} ({l.years})</option>)}
                    </select>
                    {newYears && <p style={styles.levelSuggestion}>💡 Suggested: {getLevelFromYears(parseInt(newYears))} based on {newYears} years</p>}
                  </div>
                </div>
                {newLevel && <div style={styles.levelInfoBox}>{LEVELS.find(l => l.value === newLevel)?.description}</div>}
                <label style={styles.fieldLabel}>Industry * (select all that apply)</label>
                <div style={styles.checkGrid}>
                  {INDUSTRIES.map(industry => (
                    <label key={industry} style={styles.checkLabel}>
                      <input type="checkbox" checked={newIndustries.includes(industry)} onChange={() => toggleIndustry(industry)} style={{ accentColor: '#E94560' }} />
                      {industry}
                    </label>
                  ))}
                </div>
                {selectedTradeInfo && (
                  <>
                    <label style={styles.fieldLabel}>Specializations (select all that apply)</label>
                    <div style={styles.specGrid}>
                      {selectedTradeInfo.specializations.map(spec => (
                        <label key={spec} style={styles.checkLabel}>
                          <input type="checkbox" checked={newSpecializations.includes(spec)} onChange={() => toggleSpecialization(spec)} style={{ accentColor: '#E94560' }} />
                          {spec}
                          {selectedTradeInfo.licensedSpecializations?.includes(spec) && <span style={styles.licenseTag}>🏛️ Cert</span>}
                        </label>
                      ))}
                    </div>
                  </>
                )}
                {selectedTradeInfo?.requiresLicense && newLevel === 'Master' && (
                  <div style={styles.licenseUploadSection}>
                    <h4 style={styles.licenseUploadTitle}>🏛️ Contractor License Upload</h4>
                    <p style={styles.licenseUploadSubtitle}>Upload your {selectedTradeInfo.licenseBoard} contractor license.</p>
                    <div style={styles.row}>
                      <div style={{ flex: 1 }}>
                        <label style={styles.fieldLabel}>License Number</label>
                        <input style={styles.input} type="text" placeholder="e.g. EC-12345" value={newLicenseNumber} onChange={e => setNewLicenseNumber(e.target.value)} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={styles.fieldLabel}>License State</label>
                        <select style={styles.input} value={newLicenseState} onChange={e => setNewLicenseState(e.target.value)}>
                          {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={styles.row}>
                      <div style={{ flex: 1 }}>
                        <label style={styles.fieldLabel}>License Type</label>
                        <input style={styles.input} type="text" placeholder="e.g. Limited, Intermediate, Unlimited" value={newLicenseType} onChange={e => setNewLicenseType(e.target.value)} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={styles.fieldLabel}>Expiration Date</label>
                        <input style={styles.input} type="date" value={newLicenseExpiry} onChange={e => setNewLicenseExpiry(e.target.value)} />
                      </div>
                    </div>
                    <label style={styles.docUploadBtn}>
                      📄 {newLicenseDoc ? '✅ Document Uploaded' : 'Upload License Document'}
                      <input type="file" accept="image/*,.pdf" onChange={handleLicenseDocUpload} style={{ display: 'none' }} />
                    </label>
                  </div>
                )}
                <div style={styles.formButtons}>
                  <button style={styles.cancelBtn} onClick={() => { setShowAddTrade(false); setNewTrade(''); setNewLevel(''); setNewYears(''); setNewIndustries([]); setNewSpecializations([]); setNewLicenseNumber(''); setNewLicenseDoc(''); }}>Cancel</button>
                  <button style={styles.addTradeBtn} onClick={addTradeProfile}>+ Add Trade Profile</button>
                </div>
              </div>
            ) : (
              <button style={styles.addTradeBtnMain} onClick={() => setShowAddTrade(true)}>+ Add Trade</button>
            )}
            <div style={styles.nextBtn}>
              <button style={styles.secondaryBtn} onClick={() => setActiveSection('basic')}>← Back</button>
              <button style={styles.primaryBtn} onClick={() => setActiveSection('certifications')}>Next: Certifications →</button>
            </div>
          </div>
        )}

        {/* CERTIFICATIONS */}
        {activeSection === 'certifications' && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Certifications & Safety Cards</h3>
            <p style={styles.sectionSubtitle}>Select all certifications you hold.</p>
            <div style={styles.certGrid}>
              {CERTIFICATIONS.map(cert => (
                <label key={cert} style={certifications.includes(cert) ? styles.certLabelActive : styles.certLabel}>
                  <input type="checkbox" checked={certifications.includes(cert)} onChange={() => toggleCertification(cert)} style={{ display: 'none' }} />
                  {certifications.includes(cert) ? '✅' : '○'} {cert}
                </label>
              ))}
            </div>
            <div style={styles.nextBtn}>
              <button style={styles.secondaryBtn} onClick={() => setActiveSection('trades')}>← Back</button>
              <button style={styles.primaryBtn} onClick={() => setActiveSection('work')}>Next: Work Preferences →</button>
            </div>
          </div>
        )}

        {/* WORK PREFERENCES */}
        {activeSection === 'work' && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Work Preferences</h3>
            <div style={styles.row}>
              <div style={{ flex: 1 }}>
                <label style={styles.fieldLabel}>Your Hourly Rate ($) *</label>
                <input style={styles.input} type="number" min="15" max="200" placeholder="e.g. 35" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} />
                {hourlyRate && <p style={styles.rateNote}>Contractors see ${Math.ceil(parseFloat(hourlyRate) * 1.15)}/hr</p>}
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.fieldLabel}>Service Radius (miles)</label>
                <select style={styles.input} value={radius} onChange={e => setRadius(e.target.value)}>
                  {['10', '15', '25', '50', '75', '100'].map(r => <option key={r} value={r}>Within {r} miles</option>)}
                </select>
              </div>
            </div>
            <label style={styles.fieldLabel}>Service Area ZIP Code</label>
            <input style={styles.input} type="text" placeholder="Primary ZIP code for work" maxLength={5} value={zipCode} onChange={e => setZipCode(e.target.value)} />
            <label style={styles.fieldLabel}>Professional Bio</label>
            <textarea style={styles.textarea} placeholder="Describe your experience..." value={bio} onChange={e => setBio(e.target.value)} rows={4} />
            <div style={styles.nextBtn}>
              <button style={styles.secondaryBtn} onClick={() => setActiveSection('certifications')}>← Back</button>
              <button style={styles.primaryBtn} onClick={() => setActiveSection('compliance')}>Next: Compliance →</button>
            </div>
          </div>
        )}

        {/* COMPLIANCE */}
        {activeSection === 'compliance' && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Legal Compliance</h3>
            <p style={styles.sectionSubtitle}>All items must be confirmed before your profile goes active.</p>
            <div style={styles.agreementsList}>
              {WORKER_AGREEMENTS.map(agreement => (
                <div key={agreement.id}
                  style={complianceAgreements[agreement.id] ? styles.agreementItemChecked : styles.agreementItem}
                  onClick={() => toggleComplianceAgreement(agreement.id)}>
                  <div style={styles.agreementCheckbox}>
                    {complianceAgreements[agreement.id]
                      ? <span style={styles.checkboxChecked}>✓</span>
                      : <span style={styles.checkboxUnchecked} />}
                  </div>
                  <div style={styles.agreementTextBlock}>
                    <p style={styles.agreementText}>{agreement.text}</p>
                    <span style={styles.requiredTag}>Required</span>
                  </div>
                </div>
              ))}
            </div>
            <div
              style={insuranceVerified ? styles.agreementItemChecked : styles.agreementItem}
              onClick={() => setInsuranceVerified(!insuranceVerified)}>
              <div style={styles.agreementCheckbox}>
                {insuranceVerified ? <span style={styles.checkboxChecked}>✓</span> : <span style={styles.checkboxUnchecked} />}
              </div>
              <div style={styles.agreementTextBlock}>
                <p style={styles.agreementText}>I carry general liability insurance and can provide a certificate upon request</p>
                <span style={styles.optionalTag}>Optional</span>
              </div>
            </div>
            {insuranceVerified && (
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.fieldLabel}>Upload Certificate of Insurance (Optional)</label>
                <label style={styles.docUploadBtn}>
                  📄 {insuranceDoc ? '✅ Insurance Document Uploaded' : 'Upload COI Document'}
                  <input type="file" accept="image/*,.pdf" onChange={handleInsuranceUpload} style={{ display: 'none' }} />
                </label>
              </div>
            )}
            {!allComplianceChecked && (
              <div style={styles.complianceWarning}>⚠️ All required agreements must be acknowledged before your profile can go active.</div>
            )}
            <div style={styles.registrationInfo}>
              <p style={styles.registrationTitle}>📋 How Your Profile Becomes Active</p>
              <p style={styles.registrationText}>{`• No license required trades → active immediately\n• Helper / Apprentice / Journeyman → active immediately\n• Master / Foreman → active immediately, contractor license upload unlocks "Licensed Contractor" badge\n• License documents reviewed within 1–2 business days`}</p>
            </div>
            <div style={styles.saveSection}>
              <button style={saved ? styles.savedBtnLarge : styles.saveBtnLarge} onClick={handleSave} disabled={saving}>
                {saved ? '✅ Profile Saved!' : saving ? 'Saving...' : '💾 Save Profile'}
              </button>
            </div>

            <div style={styles.deleteSection}>
              <div style={styles.deleteDivider} />
              <h4 style={styles.deleteTitle}>⚠️ Delete Account</h4>
              <p style={styles.deleteText}>
                Permanently delete your TradePower account. This action cannot be undone.
                Financial records are retained for tax and legal compliance.
              </p>
              {!showDeleteConfirm ? (
                <button style={styles.deleteBtn} onClick={() => { setShowDeleteConfirm(true); setDeleteError(''); setDeleteConfirmText(''); }}>
                  Delete My Account
                </button>
              ) : (
                <div style={styles.deleteConfirmBox}>
                  <p style={styles.deleteConfirmTitle}>⚠️ Are you absolutely sure?</p>
                  <div style={styles.deleteConfirmList}>
                    <p style={styles.deleteConfirmItem}>✗ Your profile will be permanently deleted</p>
                    <p style={styles.deleteConfirmItem}>✗ You will lose access to all job history</p>
                    <p style={styles.deleteConfirmItem}>✗ This cannot be reversed</p>
                    <p style={{ ...styles.deleteConfirmItem, color: '#28a745' }}>✓ Financial records kept for tax compliance</p>
                  </div>
                  <label style={styles.deleteConfirmLabel}>
                    Type <strong>DELETE</strong> to confirm:
                  </label>
                  <input
                    style={styles.deleteConfirmInput}
                    type="text"
                    placeholder="DELETE"
                    value={deleteConfirmText}
                    onChange={e => setDeleteConfirmText(e.target.value)}
                  />
                  {deleteError && <p style={styles.deleteError}>{deleteError}</p>}
                  <div style={styles.deleteConfirmButtons}>
                    <button
                      style={styles.deleteCancelBtn}
                      onClick={() => { setShowDeleteConfirm(false); setDeleteError(''); setDeleteConfirmText(''); }}
                      disabled={deleting}>
                      Cancel
                    </button>
                    <button
                      style={deleting ? styles.deleteConfirmBtnDisabled : styles.deleteConfirmBtn}
                      onClick={handleDeleteAccount}
                      disabled={deleting || deleteConfirmText !== 'DELETE'}>
                      {deleting ? '⏳ Deleting...' : '🗑 Permanently Delete Account'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* PAYMENTS TAB */}
        {activeSection === 'payments' && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>💰 Payments & Payouts</h3>
            <p style={styles.sectionSubtitle}>
              Manage your bank account and view your earnings history.
              Payouts are sent every Thursday for the prior work week (Sunday–Saturday).
            </p>
            <div style={stripeOnboardingDone && stripePayoutsEnabled ? styles.stripeConnectedCard : styles.stripeNotConnectedCard}>
              {stripeOnboardingDone && stripePayoutsEnabled ? (
                <>
                  <div style={styles.stripeStatusRow}>
                    <span style={styles.stripeStatusIcon}>✅</span>
                    <div>
                      <p style={styles.stripeStatusTitle}>Bank Account Connected</p>
                      <p style={styles.stripeStatusText}>Your bank account is connected and payouts are enabled.</p>
                    </div>
                  </div>
                  <button style={styles.stripeUpdateBtn} onClick={handleStartStripeOnboarding} disabled={stripeRedirecting}>
                    {stripeRedirecting ? '⏳ Redirecting...' : '🏦 Update Bank Account'}
                  </button>
                </>
              ) : stripeOnboardingDone && !stripePayoutsEnabled ? (
                <>
                  <div style={styles.stripeStatusRow}>
                    <span style={styles.stripeStatusIcon}>⏳</span>
                    <div>
                      <p style={styles.stripeStatusTitle}>Onboarding Incomplete</p>
                      <p style={styles.stripeStatusText}>Complete Stripe onboarding to start receiving payouts.</p>
                    </div>
                  </div>
                  <button style={styles.stripeConnectBtn} onClick={handleStartStripeOnboarding} disabled={stripeRedirecting}>
                    {stripeRedirecting ? '⏳ Redirecting...' : '🏦 Complete Bank Setup →'}
                  </button>
                </>
              ) : (
                <>
                  <div style={styles.stripeStatusRow}>
                    <span style={styles.stripeStatusIcon}>⚠️</span>
                    <div>
                      <p style={styles.stripeStatusTitle}>Bank Account Not Connected</p>
                      <p style={styles.stripeStatusText}>Connect your bank account to receive weekly payouts. Required before you can accept jobs.</p>
                    </div>
                  </div>
                  <div style={styles.stripeFeatures}>
                    <div style={styles.stripeFeature}><span>💰</span><span>Paid every Thursday for confirmed hours</span></div>
                    <div style={styles.stripeFeature}><span>📋</span><span>Stripe issues your 1099-NEC automatically over $600/year</span></div>
                    <div style={styles.stripeFeature}><span>🔒</span><span>TradePower never sees your bank details</span></div>
                  </div>
                  <button style={stripeRedirecting ? styles.stripeConnectBtnLoading : styles.stripeConnectBtn} onClick={handleStartStripeOnboarding} disabled={stripeRedirecting}>
                    {stripeRedirecting ? '⏳ Redirecting to Stripe...' : '🏦 Connect Bank Account →'}
                  </button>
                </>
              )}
            </div>
            {stripeOnboardingDone && (
              <>
                <div style={styles.earningsSummaryRow}>
                  <div style={styles.earningsSummaryCard}>
                    <span style={styles.earningsSummaryAmount}>${totalEarningsAllTime.toLocaleString()}</span>
                    <span style={styles.earningsSummaryLabel}>Total Paid Out</span>
                  </div>
                  <div style={{ ...styles.earningsSummaryCard, border: '1px solid #ffc107' }}>
                    <span style={{ ...styles.earningsSummaryAmount, color: '#856404' }}>${pendingEarnings.toLocaleString()}</span>
                    <span style={styles.earningsSummaryLabel}>Pending Payout</span>
                  </div>
                  <div style={styles.earningsSummaryCard}>
                    <span style={styles.earningsSummaryAmount}>{payrollLedger.filter(e => e.status === 'paid').reduce((sum, e) => sum + (e.confirmedHours || 0), 0)}</span>
                    <span style={styles.earningsSummaryLabel}>Total Hours Paid</span>
                  </div>
                </div>
                <h4 style={styles.ledgerTitle}>Earnings History</h4>
                {ledgerLoading ? (
                  <p style={styles.ledgerLoading}>Loading earnings...</p>
                ) : payrollLedger.length === 0 ? (
                  <div style={styles.ledgerEmpty}>
                    <p style={styles.ledgerEmptyIcon}>📊</p>
                    <p style={styles.ledgerEmptyTitle}>No earnings yet</p>
                    <p style={styles.ledgerEmptyText}>Your earnings will appear here after your first confirmed work day.</p>
                  </div>
                ) : (
                  <div style={styles.ledgerList}>
                    {getLedgerByWeek().map(week => (
                      <div key={week.weekEnding} style={styles.weekCard}>
                        <div style={styles.weekCardHeader}>
                          <div>
                            <p style={styles.weekTitle}>Week of {new Date(week.weekEnding + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                            <p style={styles.weekSubtitle}>Payout date: {week.payoutDate ? new Date(week.payoutDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'TBD'}</p>
                          </div>
                          <div style={styles.weekRight}>
                            <span style={week.status === 'paid' ? styles.paidBadge : styles.pendingBadge}>{week.status === 'paid' ? '✅ Paid' : '⏳ Pending'}</span>
                            <span style={styles.weekTotal}>${week.totalEarnings.toLocaleString()}</span>
                          </div>
                        </div>
                        <div style={styles.weekDays}>
                          {week.entries.sort((a, b) => a.date?.localeCompare(b.date)).map(entry => (
                            <div key={entry.id} style={styles.weekDayRow}>
                              <span style={styles.weekDayDate}>{new Date(entry.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                              <span style={styles.weekDayJob}>{entry.jobDescription || 'Job'}</span>
                              <span style={styles.weekDayHours}>{entry.confirmedHours}h</span>
                              <span style={styles.weekDayEarnings}>${entry.workerEarnings}</span>
                            </div>
                          ))}
                        </div>
                        <div style={styles.weekTotalRow}>
                          <span style={styles.weekTotalLabel}>Total</span>
                          <span style={styles.weekTotalHours}>{week.totalHours}h</span>
                          <span style={styles.weekTotalAmount}>${week.totalEarnings}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  loadingContainer: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  navbar: { backgroundColor: 'white', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', position: 'sticky', top: 0, zIndex: 100 },
  logo: { fontSize: '24px', fontWeight: 'bold', color: '#E94560', margin: 0 },
  navRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  saveBtn: { padding: '10px 20px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  savingBtn: { padding: '10px 20px', backgroundColor: '#ccc', color: 'white', border: 'none', borderRadius: '8px', cursor: 'not-allowed', fontSize: '14px', fontWeight: 'bold' },
  savedBtn: { padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  backButton: { padding: '8px 16px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer' },
  content: { maxWidth: '800px', margin: '0 auto', padding: '40px 24px' },
  header: { marginBottom: '24px' },
  title: { fontSize: '28px', color: '#1a1a2e', marginBottom: '8px' },
  subtitle: { color: '#666' },
  mismatchWarning: { backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', fontSize: '14px', color: '#856404' },
  statusPanel: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '20px', marginBottom: '20px', border: '2px solid #28a745' },
  statusPanelHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' },
  statusPanelTitle: { fontSize: '17px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '4px' },
  statusPanelSubtitle: { fontSize: '13px', color: '#666' },
  activeBadge: { backgroundColor: '#d4edda', color: '#155724', padding: '6px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold' },
  inactiveBadge: { backgroundColor: '#fff3cd', color: '#856404', padding: '6px 16px', borderRadius: '20px', fontSize: '13px', fontWeight: 'bold' },
  statusNotActive: { fontSize: '14px', color: '#666', lineHeight: '1.6' },
  statusChecklist: { display: 'flex', gap: '12px', marginTop: '10px', flexWrap: 'wrap' },
  checkDone: { backgroundColor: '#d4edda', color: '#155724', padding: '4px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold' },
  checkMissing: { backgroundColor: '#f8f9fa', color: '#999', padding: '4px 12px', borderRadius: '8px', fontSize: '13px', border: '1px dashed #ddd' },
  statusTradeList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  statusTradeRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '12px 14px', gap: '12px', flexWrap: 'wrap' },
  statusTradeInfo: { display: 'flex', flexDirection: 'column', gap: '2px', flex: 1 },
  statusTradeName: { fontSize: '15px', fontWeight: 'bold', color: '#1a1a2e' },
  statusTradeLevel: { fontSize: '12px', color: '#666' },
  statusTradeSpecs: { fontSize: '11px', color: '#999' },
  statusTradeBadgeCol: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 },
  statusTradeBadge: { padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 'bold' },
  statusTradeDetail: { fontSize: '11px', color: '#999', textAlign: 'right', maxWidth: '200px' },
  statusCertRow: { display: 'flex', alignItems: 'flex-start', gap: '8px', paddingTop: '10px', borderTop: '1px solid #e0e0e0', flexWrap: 'wrap' },
  statusCertLabel: { fontSize: '13px', fontWeight: 'bold', color: '#555', flexShrink: 0 },
  statusCertTags: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  statusCertTag: { backgroundColor: '#d4edda', color: '#155724', padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold' },
  statusRateRow: { display: 'flex', alignItems: 'center', gap: '12px', paddingTop: '10px', borderTop: '1px solid #e0e0e0' },
  statusRateLabel: { fontSize: '13px', fontWeight: 'bold', color: '#555' },
  statusRateValue: { fontSize: '16px', fontWeight: 'bold', color: '#28a745' },
  statusRateContractor: { fontSize: '12px', color: '#999' },
  sectionNav: { display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap', backgroundColor: 'white', borderRadius: '12px', padding: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  sectionNavBtn: { padding: '8px 16px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  sectionNavBtnActive: { padding: '8px 16px', backgroundColor: '#E94560', color: 'white', border: '1px solid #E94560', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },
  section: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '28px' },
  sectionTitle: { fontSize: '20px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '8px' },
  sectionSubtitle: { color: '#666', fontSize: '14px', marginBottom: '24px' },
  subheading: { fontSize: '15px', fontWeight: 'bold', color: '#1a1a2e', marginTop: '16px', marginBottom: '10px' },
  photoSection: { display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '24px' },
  photo: { width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #E94560' },
  photoPlaceholder: { width: '100px', height: '100px', borderRadius: '50%', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  uploadBtn: { display: 'inline-block', padding: '10px 16px', backgroundColor: '#E94560', color: 'white', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  docUploadBtn: { display: 'inline-block', padding: '10px 16px', backgroundColor: '#1a1a2e', color: 'white', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' },
  uploadNote: { fontSize: '12px', color: '#999', marginTop: '6px' },
  row: { display: 'flex', gap: '16px' },
  fieldLabel: { display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#555', marginBottom: '6px', marginTop: '8px' },
  input: { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '15px', boxSizing: 'border-box', marginBottom: '16px' },
  textarea: { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box', resize: 'vertical', marginBottom: '16px' },

  // SMS Toggle
  smsToggleBox: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', border: '1px solid #e0e0e0', gap: '12px' },
  smsToggleLeft: { flex: 1 },
  smsToggleTitle: { fontSize: '14px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '4px' },
  smsToggleDesc: { fontSize: '12px', color: '#666', lineHeight: '1.4' },
  smsToggleOn: { padding: '8px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', flexShrink: 0 },
  smsToggleOff: { padding: '8px 20px', backgroundColor: '#ccc', color: 'white', border: 'none', borderRadius: '20px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', flexShrink: 0 },

  emptyTrades: { textAlign: 'center', padding: '40px', color: '#666', marginBottom: '20px' },
  emptyTitle: { fontSize: '18px', color: '#1a1a2e', marginBottom: '8px' },
  emptyText: { fontSize: '14px', color: '#999' },
  tradeCard: { backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '16px', marginBottom: '16px', border: '1px solid #e0e0e0' },
  tradeCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' },
  tradeName: { fontSize: '17px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '8px' },
  tradeBadgeRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  levelBadge: { backgroundColor: '#1a1a2e', color: 'white', padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 'bold' },
  yearsBadge: { backgroundColor: '#e8f4fd', color: '#2c5282', padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 'bold' },
  licenseBadge: { padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 'bold' },
  noLicenseBadge: { backgroundColor: '#d4edda', color: '#155724', padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 'bold' },
  removeBtn: { padding: '6px 12px', backgroundColor: 'transparent', color: '#dc3545', border: '1px solid #dc3545', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', flexShrink: 0 },
  tradeDetails: { display: 'flex', flexDirection: 'column', gap: '8px' },
  tradeDetailRow: { display: 'flex', alignItems: 'flex-start', gap: '8px' },
  tradeDetailLabel: { fontSize: '12px', color: '#666', fontWeight: 'bold', minWidth: '100px', paddingTop: '4px' },
  tagRow: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
  industryTag: { backgroundColor: '#e8f4fd', color: '#2c5282', padding: '2px 8px', borderRadius: '8px', fontSize: '11px' },
  specTag: { backgroundColor: '#f0f0f0', color: '#444', padding: '2px 8px', borderRadius: '8px', fontSize: '11px' },
  licenseInfoBox: { backgroundColor: '#fff3cd', borderRadius: '8px', padding: '10px 14px', marginTop: '10px' },
  licenseInfoText: { fontSize: '13px', color: '#856404' },
  licenseInfoBanner: { backgroundColor: '#e8f4fd', border: '1px solid #bee3f8', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px', fontSize: '13px', color: '#2c5282' },
  levelInfoBox: { backgroundColor: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: '#276749' },
  checkGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '16px' },
  specGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '16px' },
  checkLabel: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#333', cursor: 'pointer' },
  licenseTag: { backgroundColor: '#fff3cd', color: '#856404', padding: '1px 6px', borderRadius: '6px', fontSize: '10px', marginLeft: '4px' },
  licenseUploadSection: { backgroundColor: '#f8f9fa', border: '1px solid #ddd', borderRadius: '10px', padding: '16px', marginBottom: '16px' },
  licenseUploadTitle: { fontSize: '15px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '6px' },
  licenseUploadSubtitle: { fontSize: '13px', color: '#666', marginBottom: '16px' },
  addTradeForm: { backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '20px', border: '2px solid #E94560', marginBottom: '16px' },
  addTradeTitle: { fontSize: '16px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '16px' },
  levelSuggestion: { fontSize: '12px', color: '#28a745', marginTop: '-10px', marginBottom: '8px' },
  formButtons: { display: 'flex', gap: '12px', marginTop: '8px' },
  cancelBtn: { flex: 1, padding: '12px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  addTradeBtn: { flex: 2, padding: '12px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  addTradeBtnMain: { width: '100%', padding: '14px', backgroundColor: 'transparent', color: '#E94560', border: '2px dashed #E94560', borderRadius: '10px', cursor: 'pointer', fontSize: '15px', fontWeight: 'bold', marginTop: '8px' },
  certGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px', marginBottom: '24px' },
  certLabel: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', backgroundColor: '#f8f9fa', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#444' },
  certLabelActive: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', backgroundColor: '#d4edda', border: '1px solid #28a745', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: '#155724', fontWeight: 'bold' },
  rateNote: { fontSize: '12px', color: '#E94560', marginTop: '-10px', marginBottom: '8px' },
  agreementsList: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '10px' },
  agreementItem: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px', backgroundColor: '#f8f9fa', borderRadius: '10px', cursor: 'pointer', border: '2px solid transparent' },
  agreementItemChecked: { display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px', backgroundColor: '#f0fff4', borderRadius: '10px', cursor: 'pointer', border: '2px solid #28a745' },
  agreementCheckbox: { flexShrink: 0, marginTop: '1px' },
  checkboxChecked: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', backgroundColor: '#28a745', borderRadius: '6px', color: 'white', fontSize: '13px', fontWeight: 'bold' },
  checkboxUnchecked: { display: 'block', width: '22px', height: '22px', border: '2px solid #ddd', borderRadius: '6px', backgroundColor: 'white' },
  agreementTextBlock: { flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' },
  agreementText: { fontSize: '13px', color: '#333', lineHeight: '1.5' },
  optionalTag: { fontSize: '11px', color: '#856404', backgroundColor: '#fff3cd', padding: '2px 8px', borderRadius: '6px', alignSelf: 'flex-start' },
  requiredTag: { fontSize: '11px', color: '#155724', backgroundColor: '#d4edda', padding: '2px 8px', borderRadius: '6px', alignSelf: 'flex-start' },
  complianceWarning: { backgroundColor: '#fff3cd', color: '#856404', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' },
  registrationInfo: { backgroundColor: '#e8f4fd', borderRadius: '10px', padding: '16px', marginBottom: '24px' },
  registrationTitle: { fontSize: '14px', fontWeight: 'bold', color: '#2c5282', marginBottom: '6px' },
  registrationText: { fontSize: '13px', color: '#2c5282', lineHeight: '2', whiteSpace: 'pre-line' },
  saveSection: { textAlign: 'center', marginBottom: '32px' },
  saveBtnLarge: { padding: '16px 48px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' },
  savedBtnLarge: { padding: '16px 48px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' },
  nextBtn: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' },
  primaryBtn: { padding: '12px 24px', backgroundColor: '#E94560', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  secondaryBtn: { padding: '12px 24px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  deleteSection: { marginTop: '8px' },
  deleteDivider: { borderTop: '1px solid #f0f0f0', marginBottom: '24px' },
  deleteTitle: { fontSize: '15px', fontWeight: 'bold', color: '#dc3545', marginBottom: '8px' },
  deleteText: { fontSize: '13px', color: '#666', lineHeight: '1.5', marginBottom: '14px' },
  deleteBtn: { padding: '10px 20px', backgroundColor: 'transparent', color: '#dc3545', border: '1px solid #dc3545', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },
  deleteConfirmBox: { backgroundColor: '#fff5f5', border: '2px solid #dc3545', borderRadius: '10px', padding: '20px' },
  deleteConfirmTitle: { fontSize: '15px', fontWeight: 'bold', color: '#dc3545', marginBottom: '12px' },
  deleteConfirmList: { marginBottom: '16px' },
  deleteConfirmItem: { fontSize: '13px', color: '#721c24', marginBottom: '4px' },
  deleteConfirmLabel: { display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#555', marginBottom: '8px' },
  deleteConfirmInput: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '2px solid #dc3545', fontSize: '15px', boxSizing: 'border-box', marginBottom: '12px', fontWeight: 'bold', letterSpacing: '2px' },
  deleteError: { fontSize: '13px', color: '#dc3545', marginBottom: '12px', fontWeight: 'bold' },
  deleteConfirmButtons: { display: 'flex', gap: '10px' },
  deleteCancelBtn: { flex: 1, padding: '11px', backgroundColor: 'transparent', color: '#666', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  deleteConfirmBtn: { flex: 2, padding: '11px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  deleteConfirmBtnDisabled: { flex: 2, padding: '11px', backgroundColor: '#ccc', color: 'white', border: 'none', borderRadius: '8px', cursor: 'not-allowed', fontSize: '14px', fontWeight: 'bold' },
  stripeConnectedCard: { backgroundColor: '#f0fff4', border: '2px solid #28a745', borderRadius: '12px', padding: '20px', marginBottom: '24px' },
  stripeNotConnectedCard: { backgroundColor: '#fff8e1', border: '2px solid #FFB800', borderRadius: '12px', padding: '20px', marginBottom: '24px' },
  stripeStatusRow: { display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '16px' },
  stripeStatusIcon: { fontSize: '28px', flexShrink: 0 },
  stripeStatusTitle: { fontSize: '16px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '4px' },
  stripeStatusText: { fontSize: '13px', color: '#555', lineHeight: '1.5' },
  stripeFeatures: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' },
  stripeFeature: { display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', color: '#555', backgroundColor: 'white', borderRadius: '8px', padding: '8px 12px' },
  stripeConnectBtn: { width: '100%', padding: '13px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  stripeConnectBtnLoading: { width: '100%', padding: '13px', backgroundColor: '#ccc', color: 'white', border: 'none', borderRadius: '10px', cursor: 'not-allowed', fontSize: '14px', fontWeight: 'bold' },
  stripeUpdateBtn: { padding: '10px 20px', backgroundColor: 'transparent', color: '#28a745', border: '1px solid #28a745', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' },
  earningsSummaryRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '24px' },
  earningsSummaryCard: { backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '16px', textAlign: 'center', border: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column', gap: '4px' },
  earningsSummaryAmount: { fontSize: '24px', fontWeight: 'bold', color: '#28a745' },
  earningsSummaryLabel: { fontSize: '12px', color: '#999' },
  ledgerTitle: { fontSize: '16px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '14px' },
  ledgerLoading: { fontSize: '14px', color: '#666', textAlign: 'center', padding: '20px' },
  ledgerEmpty: { textAlign: 'center', padding: '40px' },
  ledgerEmptyIcon: { fontSize: '40px', marginBottom: '10px' },
  ledgerEmptyTitle: { fontSize: '16px', color: '#1a1a2e', fontWeight: 'bold', marginBottom: '6px' },
  ledgerEmptyText: { fontSize: '13px', color: '#999' },
  ledgerList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  weekCard: { backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '16px', border: '1px solid #e0e0e0' },
  weekCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' },
  weekTitle: { fontSize: '14px', fontWeight: 'bold', color: '#1a1a2e', marginBottom: '2px' },
  weekSubtitle: { fontSize: '12px', color: '#999' },
  weekRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' },
  paidBadge: { backgroundColor: '#d4edda', color: '#155724', padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold' },
  pendingBadge: { backgroundColor: '#fff3cd', color: '#856404', padding: '3px 10px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold' },
  weekTotal: { fontSize: '18px', fontWeight: 'bold', color: '#28a745' },
  weekDays: { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' },
  weekDayRow: { display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'white', borderRadius: '6px', padding: '8px 10px' },
  weekDayDate: { fontSize: '12px', color: '#555', width: '100px', flexShrink: 0 },
  weekDayJob: { fontSize: '12px', color: '#666', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  weekDayHours: { fontSize: '12px', color: '#999', width: '30px', textAlign: 'center', flexShrink: 0 },
  weekDayEarnings: { fontSize: '13px', fontWeight: 'bold', color: '#28a745', width: '60px', textAlign: 'right', flexShrink: 0 },
  weekTotalRow: { display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#d4edda', borderRadius: '6px', padding: '8px 10px' },
  weekTotalLabel: { fontSize: '13px', fontWeight: 'bold', color: '#155724', flex: 1 },
  weekTotalHours: { fontSize: '13px', fontWeight: 'bold', color: '#155724', width: '30px', textAlign: 'center', flexShrink: 0 },
  weekTotalAmount: { fontSize: '15px', fontWeight: 'bold', color: '#155724', width: '60px', textAlign: 'right', flexShrink: 0 },
};

export default WorkerProfile;